import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  toErrorMessage,
  useElementClickedTracking,
  useErrorTracking,
  useFileUploadedTracking,
} from "@/lib/analytics/useTracking";
import { ANALYTICS_PLATFORM_VARIANT } from "@/lib/constants";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import {
  annotationCreateBatch,
  documentCreate,
  internalNoteCreate,
  participantAdd,
  participantRoundStatusCreate,
  proofingRoundCreate,
  proofingRoundDelete,
  proofingTitleUpdate,
} from "@/proofingService-client/sdk.gen";
import { getDocumentUploadSignedUrl, uploadFileToS3 } from "../services";

const ANNOTATION_BATCH_SIZE = 200;
const EMBEDDED_ANNOTATION_USER_ID = "00000000-0000-0000-0000-000000000001";
const EMBEDDED_ANNOTATION_USER_NAME = "Embedded";

/** Maximum number of PDF documents allowed per proofing round */
export const MAX_DOCUMENTS_PER_ROUND = 6;

export interface FileWithAnnotations {
  file: File;
  extractedAnnotations?: string[];
}

export interface StartProofingRoundInput {
  titleId: string;
  titleName: string;
  files: FileWithAnnotations[];
  dueDate: Date;
  internalNote?: string;
  participantUserIds: string[];
  existingParticipantIds?: string[];
}

export interface StartProofingRoundResult {
  roundId: string;
  titleId: string;
}

export interface FileUploadError extends Error {
  name: "FileUploadError";
  originalError?: Error;
}

export function createFileUploadError(message: string, originalError?: Error): FileUploadError {
  const error = new Error(message) as FileUploadError;
  error.name = "FileUploadError";
  error.originalError = originalError;
  return error;
}

export function isFileUploadError(error: unknown): error is FileUploadError {
  return error instanceof Error && error.name === "FileUploadError";
}

// ─── Orchestration Steps ──────────────────────────────────────────────────────

type UploadedFile = { file: File; documentPath: string; extractedAnnotations?: string[] };

async function createRound(titleId: string, dueDate: Date): Promise<string> {
  const response = await proofingRoundCreate({
    path: { titleId },
    body: { dueDate: format(dueDate, "yyyy-MM-dd"), state: "reviewing" },
  });
  if (!response.data) throw new Error("Failed to create proofing round");
  return response.data.id;
}

async function uploadFiles(
  files: FileWithAnnotations[],
  titleId: string,
  titleName: string,
  roundId: string,
  onFileUploaded?: (file: File, roundId: string) => void,
): Promise<UploadedFile[]> {
  const uploaded: UploadedFile[] = [];

  for (const { file, extractedAnnotations } of files) {
    const { url: signedUrl, documentPath } = await getDocumentUploadSignedUrl({
      proofingTitleId: titleId,
      proofingTitleName: titleName,
      roundId,
      filename: file.name,
    });

    try {
      await uploadFileToS3(signedUrl, file);
      onFileUploaded?.(file, roundId);
    } catch (uploadError) {
      let roundDeleted = true;
      try {
        await proofingRoundDelete({ path: { titleId, roundId } });
      } catch (deleteError) {
        console.error("Failed to rollback proofing round after upload failure:", deleteError);
        roundDeleted = false;
      }
      throw createFileUploadError(
        `Failed to upload file "${file.name}". ${roundDeleted ? "The proofing round was not created." : ""}`,
        uploadError instanceof Error ? uploadError : new Error(String(uploadError)),
      );
    }

    uploaded.push({ file, documentPath, extractedAnnotations });
  }

  return uploaded;
}

async function addParticipants(
  titleId: string,
  roundId: string,
  participantUserIds: string[],
  existingParticipantIds: string[],
): Promise<void> {
  const existing = new Set(existingParticipantIds);
  for (const userId of participantUserIds) {
    if (!existing.has(userId)) {
      await participantAdd({ path: { titleId }, body: { userId } });
    }
    await participantRoundStatusCreate({ path: { roundId, userId }, body: {} });
  }
}

async function createDocumentsWithAnnotations(
  roundId: string,
  uploadedFiles: UploadedFile[],
): Promise<void> {
  for (const { file, documentPath, extractedAnnotations } of uploadedFiles) {
    const response = await documentCreate({
      path: { roundId },
      body: { documentPath, originalFilename: file.name },
    });
    if (!response.data) throw new Error(`Failed to create document record for "${file.name}"`);
    const documentId = response.data.id;

    if (extractedAnnotations && extractedAnnotations.length > 0) {
      for (let i = 0; i < extractedAnnotations.length; i += ANNOTATION_BATCH_SIZE) {
        const batch = extractedAnnotations.slice(i, i + ANNOTATION_BATCH_SIZE);
        await annotationCreateBatch({
          path: { documentId },
          body: {
            userId: EMBEDDED_ANNOTATION_USER_ID,
            userName: EMBEDDED_ANNOTATION_USER_NAME,
            annotations: batch,
          },
        });
      }
    }
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseStartProofingRoundOptions {
  onSuccess?: (data: StartProofingRoundResult) => void;
  onError?: (error: Error) => void;
}

export const useStartProofingRound = (options?: UseStartProofingRoundOptions) => {
  const queryClient = useQueryClient();
  const trackElementClicked = useElementClickedTracking();
  const trackFileUploaded = useFileUploadedTracking();
  const trackError = useErrorTracking();

  return useMutation({
    mutationFn: async (input: StartProofingRoundInput) => {
      const {
        titleId,
        titleName,
        files,
        dueDate,
        internalNote,
        participantUserIds,
        existingParticipantIds = [],
      } = input;

      if (files.length === 0) throw new Error("At least one file is required");
      if (files.length > MAX_DOCUMENTS_PER_ROUND) {
        throw new Error(`Maximum ${MAX_DOCUMENTS_PER_ROUND} files allowed per round`);
      }

      const roundId = await createRound(titleId, dueDate);

      const onFileUploaded = (file: File, rId: string) => {
        const extension = file.name.split(".").pop();
        trackFileUploaded({
          name: file.name,
          ...(extension && { extension }),
          sizeMb: file.size / (1024 * 1024),
          module: ANALYTICS_PLATFORM_VARIANT,
          itemId: rId,
        });
      };

      const uploadedFiles = await uploadFiles(files, titleId, titleName, roundId, onFileUploaded);

      await proofingTitleUpdate({ path: { titleId }, body: { state: "active" } });

      if (internalNote) {
        await internalNoteCreate({
          path: { titleId },
          body: { proofingRoundId: roundId, note: internalNote },
        });
      }

      await addParticipants(titleId, roundId, participantUserIds, existingParticipantIds);
      await createDocumentsWithAnnotations(roundId, uploadedFiles);

      return { roundId, titleId };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(data.titleId) });
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      trackElementClicked({
        elementId: "round-started",
        metadata: {
          titleId: data.titleId,
          roundId: data.roundId,
          documentCount: variables.files.length,
          participantCount: variables.participantUserIds.length,
        },
      });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to start proofing round:", error);
      trackError({ type: "round-error", action: "start", error: toErrorMessage(error) });
      options?.onError?.(error);
    },
  });
};
